/**
 * @file poolApi.js
 * @description Module to manage pool-related API calls such as fetching pools, handling chat messages, updating status, and refunds.
 * @created 12-02-2025
 * @author Govind Dindewad
 */

import axios from "axios";
import apiUrl from "./api";

/**
 * Fetches a list of pools based on various search filters.
 * @param {Object} params - The parameters for searching pools.
 * @param {string} [params.searchKey=''] - The search key to filter pools.
 * @param {string} [params.type='All'] - The type of pool to filter.
 * @param {string} [params.status='All'] - The status of the pool.
 * @param {boolean} [params.vacancy=false] - Whether to filter based on vacancy status.
 * @param {number} [params.pageNo=0] - The page number for pagination.
 * @returns {Promise<Object>} - The response object from the API call or error response.
 */
async function getPoolList({ searchKey = '', type = 'All', status = 'All', vacancy = false, pageNo = 0 }) {
    // Retrieve the authentication token from localStorage for secure API access.
    const token = window.localStorage.getItem('token');

    try {
        // Make a GET request to the API with the provided filters.
        const response = await axios.get(`${apiUrl}secure/pool/retain-all-pools?key=${searchKey}&type=${type}&status=${status}&vacancy=${vacancy}&page=${pageNo}&size=20`, {
            headers: {
                'Authorization': `Bearer ${token}`, // Attach token to the Authorization header
            }
        });
        return response;  // Return the successful response from the API
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        // Return error response if the request fails
        return error.response;
    }
}

/**
 * Fetches a list of pools for a specific plan, with various filters applied.
 * @param {Object} params - The parameters for searching pools.
 * @param {string} [params.planId=''] - The plan ID to filter pools.
 * @param {string} [params.searchKey=''] - The search key to filter pools.
 * @param {string} [params.type='All'] - The type of pool to filter.
 * @param {string} [params.status='All'] - The status of the pool.
 * @param {boolean} [params.vacancy=false] - Whether to filter based on vacancy status.
 * @param {number} [params.pageNo=0] - The page number for pagination.
 * @returns {Promise<Object>} - The response object from the API call or error response.
 */
async function getPoolListbyplan({ planId = '', searchKey = '', type = 'All', status = 'All', vacancy = false, pageNo = 0 }) {
    // Retrieve the authentication token from localStorage
    const token = window.localStorage.getItem('token');

    try {
        // Make a GET request to the API to fetch pool data based on the provided planId and filters.
        const response = await axios.get(`${apiUrl}secure/pool/${planId}/retain-all-pools?key=${searchKey}&type=${type}&status=${status}&vacancy=${vacancy}&page=${pageNo}&size=10`, {
            headers: {
                'Authorization': `Bearer ${token}`, // Use Bearer token for authentication
            }
        });
        return response;  // Return the response if successful
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        // Handle errors by returning the error response
        return error.response;
    }
}

/**
 * Retrieves detailed information about a specific pool by its ID.
 * @param {string} poolId - The ID of the pool to retrieve.
 * @returns {Promise<Object>} - The response object from the API call or error response.
 */
async function getSinglePool(poolId) {
    // Retrieve the authentication token for secure access to the pool details.
    const token = window.localStorage.getItem('token');

    try {
        // Make a GET request to fetch details of a specific pool using its poolId
        const response = await axios.get(`${apiUrl}secure/pool/${poolId}/retain`, {
            headers: {
                'Authorization': `Bearer ${token}`,  // Include the authorization token in the header
            }
        });
        return response;  // Return the API response with pool details
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        // Return the error response in case of failure
        return error.response;
    }
}

/**
 * Retrieves chat messages related to a specific pool chat.
 * @param {string} groupChatRoomId - The ID of the group chat room.
 * @param {string} userId - The ID of the user requesting the chat history.
 * @returns {Promise<Object>} - The response object from the API call or error response.
 */
async function getPoolChat(groupChatRoomId, userId) {
    // Retrieve the authentication token to access the chat messages.
    const token = window.localStorage.getItem('token');

    try {
        // Send a GET request to fetch chat messages for a given chat room and user
        const response = await axios.get(`${apiUrl}secure/chat-messages/user-chat?groupChatRoomId=${groupChatRoomId}&userId=${userId}`, {
            headers: {
                'Authorization': `Bearer ${token}`, // Provide token for secure access
            }
        });
        return response;  // Return the chat messages if the request is successful
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        // Return the error response in case of failure
        return error.response;
    }
}

/**
 * Retrieves the refund status for a specific pool and user.
 * @param {string} poolId - The ID of the pool.
 * @param {string} userId - The ID of the user.
 * @returns {Promise<Object>} - The response object from the API call or error response.
 */
async function getRefundStatus(poolId, userId) {
    // Get the authentication token from localStorage for secure access.
    const token = window.localStorage.getItem('token');

    try {
        // Make a POST request to the refund status API, passing poolId and userId
        const response = await axios.post(`${apiUrl}secure/pool/refund-retain`, { poolId, userId }, {
            headers: {
                'Authorization': `Bearer ${token}`,  // Include Bearer token for security
            }
        });
        return response;  // Return the refund status response from the API
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        // In case of an error, return the error response
        return error.response;
    }
}

/**
 * Updates the suspend status of a user in a specific pool.
 * @param {string} poolId - The ID of the pool.
 * @param {string} userId - The ID of the user to be suspended.
 * @param {boolean} isSuspend - The new suspension status (true to suspend, false to unsuspend).
 * @returns {Promise<Object>} - The response object from the API call or error response.
 */
async function updateSuspend(poolId, userId, isSuspend) {
    // Get the authentication token from localStorage
    const token = window.localStorage.getItem('token');

    try {
        // Make a PUT request to suspend/unsuspend a user in a pool
        const response = await axios.put(`${apiUrl}secure/pool/suspend-member`, { poolId, userId, isSuspend }, {
            headers: {
                'Authorization': `Bearer ${token}`,  // Use token for secure API access
            }
        });
        return response;  // Return the response from the API if the request is successful
    } catch (error) {
        if (error?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            }
        }
        // In case of an error, return the error response
        return error.response;
    }
}

// Exporting all the functions for use in other modules
export { getPoolList, getPoolListbyplan, getSinglePool, getPoolChat, getRefundStatus, updateSuspend };
